import {
  TrackInfo,
  TrackType,
  AdPlayer,
  AdState,
  RequestType,
  SeekRangeInfo,
  StreamTimelineEvent,
  Action,
  StreamTimelineMetadata,
  ThumbnailPreviewMetadata,
  PlaybackOptions,
  AdBreakInfo,
  AdInfo,
  AdEvent,
  AdTrackingEvent,
  PLAYBACK_STATES,
  BUFFERING_STATES,
  SEEKING_STATES,
  TrackVariantInfo,
  Player,
  AdContentTimePosition,
  AdTechnology,
} from '@quickplay/player-interface';
import { Error, Logger, createEventEmitter, EventEmitter, PlatformUtil } from '@quickplay/foundation';
import { PlayerBuilder } from '../../PlayerBuilder';
import { IMAAdRequest } from '../../models/csai/IMAAdRequest';
import {
  createCsaiAdBreakInfo,
  createCsaiAdInfo,
  setAdsRenderingSettings,
  setImaSdkSettings,
  updateAdBreakAfterInterruption,
} from '../util/AdUtil';
import { AdsRenderingSettings } from '../../models/AdsRenderingSettings';
import { SmartTvAdManager } from '../util/ImaCsaiSmartTvAdUtil';
import { getAdfrequencyController } from '@quickplay/player';

/**
 * Creates IMAAd Player that abstracts the interactions with
 * Google IMA library.
 *
 * @param builder PlayerBuilder instance used to create the `AdPlayer` instance
 * @returns AdPlayer IMA Ad Player instance
 */
export function createIMACSAIAdsPlayer(builder: PlayerBuilder): AdPlayer {
  const logger: Logger = builder.configuredLogger;
  const eventEmitter: EventEmitter = createEventEmitter();
  const renderingSettings: AdsRenderingSettings = builder.renderingSettings;
  const videoElement = builder.videoElement;
  const containerElement = builder.containerElement;
  const imaSDK: any = builder.imaSDK;
  const adRequest: IMAAdRequest = builder.request;
  const playbackOptions: PlaybackOptions | undefined = builder.player?.playbackOptions;
  const contentPlayer: Player = builder.player;
  const afcEnabled = !!builder.adFrequencyControlConfiguration?.enabled;

  const isSmartTV = PlatformUtil.isSmartTV();
  let completedAdsDuration = 0;

  // Delay before resuming content playback after an ad ends (in milliseconds)
  // Dev Note: Some older Smart TV models (e.g., 2017-2018 LG webOS, early Tizen versions) exhibit delayed video surface handoff
  // after mid-roll ads due to IMA SDK lifecycle behavior. A minimum delay helps the SDK release the video element cleanly
  // and avoids black screens or content resume failures.
  // Default value is 500ms, can be overriden with smartTvAdPlaybackOptions?.contentPlaybackDelayMs.
  const smartTvPlaybackDelayMs: number = builder?.smartTvAdPlaybackOptions?.contentPlaybackDelayMs || 500;

  // Factor to extend the base delay (above) specifically for mid-roll to content transitions
  // Dev Note: Mid-rolls often introduce additional rendering delays vs. pre-rolls, especially on resource-constrained platforms.
  // This factor allows developers to dynamically scale the delay (e.g., 1.0 = base, 1.5 = 50% more) based on device testing results.
  // Can be configured per platform to balance responsiveness vs. transition stability.
  // Default value is 1, can be overriden with smartTvAdPlaybackOptions?.adToContentTransitionFactor.
  const midrollToContentTransitionFactor: number = builder?.smartTvAdPlaybackOptions?.adToContentTransitionFactor || 1;

  let version = '';

  let adDisplayContainer: any;
  let adsLoader: any;
  let adsManager: any = null;
  let isAdRequestPending = false;
  let adsManagerEventListener: ((evt: any) => void) | null = null;
  let adState: AdState = 'idle';
  let currentTime = 0;
  let duration = 0;
  let isBuffering = false;
  const playbackRate = 0;
  const availableBitrates: number[] = [];
  const minBitrate = 0;
  const maxBitrate = 0;
  let activeAdBreakInfo: AdBreakInfo | undefined = undefined;
  let activeAdInfo: AdInfo | undefined = undefined;
  let customPlayAdsAfterTime: number | undefined = undefined;

  let isAdBreakStarted = false;
  // Dev Note: Flag to differentiate true ad completion from ALL_ADS_COMPLETED events
  // that are triggered due to non-fatal scenarios like no valid ads in a VMAP break.
  // This helps avoid incorrectly assuming that all ads were shown when in fact
  // the ad break was skipped or failed silently.
  let nonFatalAllAdsCompletedEvent = false;

  let smartTvAdManager: SmartTvAdManager | null = null;
  let isMidrollAd = false;

  // Smart TV Continue Watching: Two-phase ads manager approach
  // Phase 1: Handle pre-rolls without playAdsAfterTime
  // Phase 2: Recreate with playAdsAfterTime for mid-rolls/post-rolls
  const isContinueWatchingSession = (): boolean => {
    return Boolean(playbackOptions?.initialPlaybackTime && playbackOptions.initialPlaybackTime > 0);
  };
  let needsAdsManagerRecreation = isSmartTV && isContinueWatchingSession();
  const hasExplicitPlayAdsAfterTime = Boolean(renderingSettings?.playAdsAfterTime !== undefined);

  let adSessionId = 0;

  function startNewAdSession(): number {
    adSessionId += 1;
    return adSessionId;
  }

  function isStaleSession(sessionId: number): boolean {
    return sessionId !== adSessionId;
  }

  // Define the IMA CSAI Ad events that we want to listen to
  const adEvents = [
    imaSDK.AdEvent.Type.ALL_ADS_COMPLETED,
    imaSDK.AdEvent.Type.COMPLETE,
    imaSDK.AdEvent.Type.FIRST_QUARTILE,
    imaSDK.AdEvent.Type.LOADED,
    imaSDK.AdEvent.Type.MIDPOINT,
    imaSDK.AdEvent.Type.PAUSED,
    imaSDK.AdEvent.Type.STARTED,
    imaSDK.AdEvent.Type.RESUMED,
    imaSDK.AdEvent.Type.AD_BUFFERING,
    imaSDK.AdEvent.Type.AD_PROGRESS,
    imaSDK.AdEvent.Type.THIRD_QUARTILE,
    imaSDK.AdEvent.Type.SKIPPED,
    imaSDK.AdEvent.Type.SKIPPABLE_STATE_CHANGED,
    imaSDK.AdEvent.Type.LOG,
    imaSDK.AdEvent.Type.CLICK,
    imaSDK.AdEvent.Type.VIDEO_CLICKED,
    imaSDK.AdEvent.Type.VIDEO_ICON_CLICKED,
    imaSDK.AdEvent.Type.IMPRESSION,
    imaSDK.AdEvent.Type.USER_CLOSE,
    imaSDK.AdEvent.Type.INTERACTION,
    imaSDK.AdEvent.Type.VOLUME_CHANGED,
    imaSDK.AdEvent.Type.VOLUME_MUTED,
    imaSDK.AdEvent.Type.AD_BREAK_READY,
  ];

  // Initialize Smart TV-specific ad manager only when running on Smart TV platforms
  // Dev Note: Smart TVs often have unique limitations around video element management, ad playback lifecycles,
  // and seek behavior when using IMA CSAI. The SmartTvAdManager is designed to handle such scenarios,
  // including buffering around cue points, mid-roll resume issues, and platform-specific seek handling.
  // Avoid initializing this logic on mobile/web to prevent unnecessary complexity or side effects.
  smartTvAdManager = ((): SmartTvAdManager | null => {
    return isSmartTV
      ? new SmartTvAdManager(contentPlayer, videoElement, builder.smartTvAdPlaybackOptions, eventEmitter, logger)
      : null;
  })();

  const getPlayerState = (): string => {
    switch (adState) {
      case 'loading':
        return PLAYBACK_STATES.LOADING;
      case 'loaded':
        return PLAYBACK_STATES.LOADED;
      case 'playing':
      case 'skipped':
      case 'started':
      case 'resumed':
      case 'completed':
      case 'failed':
        return PLAYBACK_STATES.STARTED;
      case 'paused':
        return PLAYBACK_STATES.PAUSED;
      default:
        return PLAYBACK_STATES.IDLE;
    }
  };

  /**
   * Checks if the current ad player state should prevent publishing playback state changes.
   * AdsManager will emit loaded event for each Ad. In this case, if we had mid-roll or
   * post-roll ads, AdsManager will preload mid-roll and post-roll ads and emits loaded
   * event during main content playback. This will override the content player playback
   * state during main content playback.
   *
   * @returns true if playback state publishing should be skipped, false otherwise
   */
  const shouldSkipPlaybackStatePublishing = (): boolean => {
    const adPlayerState = getPlayerState();
    return adPlayerState === PLAYBACK_STATES.LOADED || adPlayerState === PLAYBACK_STATES.LOADING;
  };

  const publishAdPlaybackState = (): void => {
    if (shouldSkipPlaybackStatePublishing()) {
      return;
    }
    const adPlayerState = getPlayerState();
    const adPlayBackState = {
      player: adPlayerState,
      seeking: SEEKING_STATES.INACTIVE,
      buffering: BUFFERING_STATES.INACTIVE,
    };
    logger.info('Player state has changed', JSON.stringify(adPlayBackState));
    eventEmitter.publish('playbackstatechanged', adPlayerState);
  };

  const publishBufferingState = (): void => {
    const adPlayerState = getPlayerState();
    const adBufferingState = isBuffering ? BUFFERING_STATES.ACTIVE : BUFFERING_STATES.INACTIVE;
    const bufferingState = {
      buffering: adBufferingState,
      seeking: SEEKING_STATES.INACTIVE,
      player: adPlayerState,
    };
    logger.info('Player state has changed', JSON.stringify(bufferingState));
    eventEmitter.publish('bufferingstatechanged', adBufferingState);
  };

  const changeState = (state: AdState, info?: any): void => {
    const previousPlayerState = getPlayerState();
    adState = state;
    if (adState === 'buffering') {
      publishBufferingState();
    } else if (adState === 'failed') {
      logger.log('aderror', info);
    } else if (previousPlayerState !== getPlayerState()) {
      publishAdPlaybackState();
    }
  };

  const imaPlayer: AdPlayer = {
    subscribe: eventEmitter.subscribe,
    unsubscribe: eventEmitter.unsubscribe,

    get playerState() {
      return getPlayerState();
    },

    get isBuffering() {
      return isBuffering;
    },

    get isSeeking() {
      return false;
    },

    get currentTime() {
      return currentTime;
    },

    get currentEpochTime() {
      return 0;
    },

    get duration() {
      return duration;
    },

    get playbackRate() {
      return playbackRate;
    },

    get availableBitrates() {
      return availableBitrates;
    },

    get minBitrate() {
      return minBitrate;
    },

    get maxBitrate() {
      return maxBitrate;
    },

    get internalPlayerVersion() {
      return version;
    },

    get internalPlayerName() {
      return 'IMA SDK';
    },

    get isLive() {
      return false;
    },

    get remainingTime() {
      return adsManager && adsManager.getRemainingTime();
    },

    get isMute() {
      return !(adsManager && adsManager.getVolume());
    },

    get volume() {
      return adsManager && adsManager.getVolume();
    },

    set volume(volumeLevel: number) {
      if (adsManager) {
        adsManager.setVolume(volumeLevel);
      }
    },

    get adInsertionType(): AdTechnology {
      return AdTechnology.CSAI;
    },

    load(): void {
      startNewAdSession();
      changeState('loading');
      requestAds(adRequest);
    },

    play(): void {
      adsManager && adsManager.resume();
    },

    pause(): void {
      adsManager && adsManager.pause();
    },

    // eslint-disable-line no-unused-vars
    abort: (_error: Error) => {
      adsManager && adsManager.destroy();
    },

    send(eventName: StreamTimelineEvent, action: Action, metadata?: StreamTimelineMetadata): void {
      logger.log(`streamTimelineEvent: ${eventName} Action: ${action} metadata: ${metadata}`);
    },

    // Optimized seek method for IMA CSAI Ad Player
    async seek(seekPoint: number): Promise<void> {
      logger.debug('[IMA CSAI] Seek requested, player state:', getPlayerState());

      // For Smart TV platforms with ad manager
      if (isSmartTV && smartTvAdManager) {
        // Skip seek during loading states to prevent playback errors
        if (shouldSkipPlaybackStatePublishing()) {
          logger.debug('[IMA CSAI][Smart TV] Seek skipped - content player is loading');
          return;
        }

        // Only allow Smart TV ad manager to handle seek when no active ad break
        if (!activeAdBreakInfo) {
          await smartTvAdManager.seek(seekPoint);
        }
      } else {
        // For non-Smart TV platforms, delegate to content player
        contentPlayer.seek(seekPoint);
      }
    },

    async getThumbnail(_position: number): Promise<ThumbnailPreviewMetadata | undefined> {
      return undefined;
    },

    getAllTracks(): TrackInfo[] {
      return [];
    },

    getActiveTracks(): TrackInfo[] {
      return [];
    },

    getVariantTracks(): TrackVariantInfo[] {
      return [];
    },

    getAvailableTrackVariants(_trackType: TrackType): TrackVariantInfo[] {
      return [];
    },

    seekableRange(): SeekRangeInfo {
      return { start: 0, end: duration };
    },

    seekToLiveEdge(): void {
      // no-op
    },

    currentOffsetFromLiveEdge(): number {
      return 0;
    },

    // eslint-disable-line no-unused-vars
    selectTrack(_track?: TrackInfo): void {
      // no-op
    },

    stop: (): Promise<void> => {
      logger.debug('[IMA CSAI] stop called');
      smartTvAdManager?.onStoppedEvent();

      // invalidate all pending ad events
      startNewAdSession();

      cleanupImaResources();
      activeAdInfo = undefined;
      activeAdBreakInfo = undefined;
      isAdBreakStarted = false;
      isMidrollAd = false;
      isBuffering = false;

      changeState('idle');
      return Promise.resolve();
    },

    muteOrUnmute(): void {
      if (adsManager) {
        if (this.isMute) {
          adsManager.setVolume(videoElement.volume);
        } else {
          adsManager.setVolume(0);
        }
      }
    },

    skipAd(): void {
      changeState('skipped');
      adsManager && adsManager.skip();
    },

    resize(width: number, height: number, isFullscreen: boolean): void {
      if (isFullscreen) {
        adsManager && adsManager.resize(width, height, imaSDK.ViewMode.FULLSCREEN);
      } else {
        adsManager && adsManager.resize(width, height, imaSDK.ViewMode.NORMAL);
      }
    },

    discardAdBreak(): void {
      adsManager && adsManager.discardAdBreak();
    },

    getCuePoints(): number[] {
      return adsManager && adsManager.getCuePoints();
    },

    setRequestInterceptor(_requestInterceptor: (requestType: RequestType, request: any) => void): void {
      //no-op
    },

    setResponseInterceptor(_responseInterceptor: (requestType: RequestType, response: any) => void): void {
      //no-op
    },

    restrictQuality(_enableRestriction: boolean): void {
      //no-op
    },

    availableMimeTypes(_track: TrackType): string[] {
      return [];
    },

    unloadAndSavePreload(): Promise<void> {
      return Promise.resolve();
    },

    restoreFromPreload(): void {
      //no-op
    },

    setPreferredMimeTypes(_trackType: TrackType, _mimeTypes: Array<string>): void {
      //no-op
    },

    accessFunctionHandler(_: string): void {
      //no-op
    },

    setPlaybackOptions(options: PlaybackOptions): void {
      logger.log(`playback options`, options);
    },
  };

  // Initializes the IMA library and sets up IMA event listerners
  function setupIMA(): void {
    const imaSdkSettings = imaSDK.settings;

    // By default, Custom Playback for iOS10+ browsers is enabled where
    // ads are played in the content player(videoElement). This causes issue
    // playing content after preroll and postroll ads after content. So we are
    // overriding IMA default settings and making it as default.
    if (imaSdkSettings) {
      imaSdkSettings.setDisableCustomPlaybackForIOS10Plus(true);
    }
    if (imaSdkSettings) {
      setImaSdkSettings(imaSdkSettings, {
        ...builder.imaSettings,
        ...(afcEnabled ? { autoPlayAdBreaks: false } : {}),
      });
    }
    adDisplayContainer = new imaSDK.AdDisplayContainer(containerElement, videoElement);
    adsLoader = new imaSDK.AdsLoader(adDisplayContainer);
    adsLoader.addEventListener(imaSDK.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED, onAdsManagerLoaded, false);
    adsLoader.addEventListener(imaSDK.AdErrorEvent.Type.AD_ERROR, onAdError, false);
    version = adsLoader.getVersion();
    // An event listener to tell the SDK that our content video
    // is completed so the SDK can play any post-roll ads.
    const contentEndedListener: any = function () {
      /**
       * DEV NOTE: SmartTV Ad Playback Limitation
       *
       * On SmartTV platforms, avoid invoking `adsLoader.contentComplete()`.
       * Doing so hands over control of the video element to the AdManager,
       * which can lead to decoder failures on SmartTVs. As a result, post-roll
       * ad playback may not be supported on these devices.
       */
      if (isAdBreakStarted && !isSmartTV) {
        adsLoader.contentComplete();
      }
    };
    videoElement.onended = contentEndedListener;
  }

  /**
   * Assigns optional properties to the IMA ads request if they are defined
   * @param _adsRequest The IMA ads request object
   * @param property The property value to check
   * @param assignFn Callback function to assign the property
   */
  function assignIfDefined<T>(_adsRequest: any, property: T | undefined, assignFn: (value: T) => void): void {
    if (property !== undefined) {
      assignFn(property);
    }
  }

  function requestAds(request: IMAAdRequest): void {
    adDisplayContainer.initialize();

    if (smartTvAdManager && playbackOptions?.initialPlaybackTime) {
      smartTvAdManager.markWatchedCuesUpTo(playbackOptions.initialPlaybackTime);

      logger.debug(`Requesting ${request.adTagUrl} with w:${request.slotWidth}, h:${request.slotHeight}`);
    }
    const adsRequest = new imaSDK.AdsRequest();
    adsRequest.adTagUrl = request.adTagUrl;

    // Optional properties
    assignIfDefined(adsRequest, request.slotWidth, value => (adsRequest.linearAdSlotWidth = value));
    assignIfDefined(adsRequest, request.slotHeight, value => (adsRequest.linearAdSlotHeight = value));
    assignIfDefined(adsRequest, request.nonLinearAdSlotWidth, value => (adsRequest.nonLinearAdSlotWidth = value));
    assignIfDefined(adsRequest, request.nonLinearAdSlotHeight, value => (adsRequest.nonLinearAdSlotHeight = value));
    assignIfDefined(adsRequest, request.adsResponse, value => (adsRequest.adsResponse = value));
    assignIfDefined(adsRequest, request.contentDuration, value => (adsRequest.contentDuration = value));
    assignIfDefined(adsRequest, request.contentTitle, value => (adsRequest.contentTitle = value));
    assignIfDefined(adsRequest, request.forceNonLinearFullSlot, value => (adsRequest.forceNonLinearFullSlot = value));
    assignIfDefined(
      adsRequest,
      request.liveStreamPrefetchSeconds,
      value => (adsRequest.liveStreamPrefetchSeconds = value),
    );
    assignIfDefined(adsRequest, request.omidAccessModeRules, value => (adsRequest.omidAccessModeRules = value));
    assignIfDefined(adsRequest, request.pageUrl, value => (adsRequest.pageUrl = value));
    assignIfDefined(adsRequest, request.vastLoadTimeout, value => (adsRequest.vastLoadTimeout = value));

    // Keywords with non-empty check
    if (request.contentKeywords !== undefined && request.contentKeywords.length > 0) {
      adsRequest.keywords = request.contentKeywords;
    }

    // Setter methods
    assignIfDefined(adsRequest, request.isAdsAutoPlay, value => adsRequest.setAdWillAutoPlay(value));
    assignIfDefined(adsRequest, request.isAdsPlayInMute, value => adsRequest.setAdWillPlayMuted(value));
    assignIfDefined(adsRequest, request.isContentPlayContinuous, value => adsRequest.setContinuousPlayback(value));

    isAdRequestPending = true;
    eventEmitter.publish(AdEvent.ADVERT_REQUEST_START);
    adsLoader.requestAds(adsRequest);
  }

  function completeAdRequest(success: boolean): void {
    if (isAdRequestPending) {
      isAdRequestPending = false;
      eventEmitter.publish(AdEvent.ADVERT_REQUEST_END, { success });
    }
  }

  /**
   * Special function for continue watching recreation with a custom playAdsAfterTime value.
   *
   * @param request - The ad request containing ad tag URL and slot dimensions.
   * @param playAdsAfterTime - The time (in seconds) after which ads should start playing.
   */

  function requestAdsWithPlayAdsAfterTime(request: IMAAdRequest, playAdsAfterTime: number): void {
    customPlayAdsAfterTime = playAdsAfterTime;
    requestAds(request);
  }

  /**
   * Initializes and starts the AdsManager
   * @param manager AdsManager
   */
  function startAdsManager(manager: any): void {
    // Attach the pause/resume events.
    // Ignore these events for smartTVs as they are handled by the ADS LOADED/COMPLETE EVENTS.
    // To address the limitation of single media element in smartTVs.
    if (!isSmartTV) {
      manager.addEventListener(imaSDK.AdEvent.Type.CONTENT_PAUSE_REQUESTED, onContentPauseRequested);
      manager.addEventListener(imaSDK.AdEvent.Type.CONTENT_RESUME_REQUESTED, onContentResumeRequested);
    }
    // Handle errors.
    manager.addEventListener(imaSDK.AdErrorEvent.Type.AD_ERROR, onAdError, false);
    manager.addEventListener(imaSDK.AdError.Type.AD_LOAD, (adError: any) => {
      logger.log('adLoadError', adError);
    });
    manager.addEventListener(imaSDK.AdError.Type.AD_PLAY, (adError: any) => {
      logger.log('adPlayError', adError);
    });

    // Attach ad event listeners with session guard to ignore stale events after stop()
    const listenerSessionId = adSessionId;
    const onAdEventWithSession = (evt: any): void => {
      if (isStaleSession(listenerSessionId)) {
        logger.debug('[IMA CSAI] Ignoring stale ad event');
        return;
      }
      onAdEvent(evt);
    };
    adsManagerEventListener = onAdEventWithSession;

    adEvents.forEach(eventType => {
      adsManager?.addEventListener(eventType, adsManagerEventListener, false);
    });

    manager.init(adRequest.slotWidth, adRequest.slotHeight, imaSDK.ViewMode.NORMAL);
    if (!afcEnabled) {
      manager.start();
    }
  }

  /**
   * Called when IMA ad manager is loaded successfully
   * @param adsManagerLoadedEvent
   */
  function onAdsManagerLoaded(adsManagerLoadedEvent: any): void {
    completeAdRequest(true);
    // Get the ads manager.
    let adsRenderingSettings = new imaSDK.AdsRenderingSettings();
    adsRenderingSettings.restoreCustomPlaybackStateOnAdBreakComplete = true;
    if (renderingSettings) {
      adsRenderingSettings = setAdsRenderingSettings(
        adsRenderingSettings,
        renderingSettings,
        imaSDK,
        customPlayAdsAfterTime,
      );
      if (renderingSettings.playAdsAfterTime === undefined && customPlayAdsAfterTime !== undefined) {
        customPlayAdsAfterTime = undefined;
      }
    }
    adsManager = adsManagerLoadedEvent.getAdsManager(videoElement, adsRenderingSettings);

    // Extract and set cue points if available (some SDKs populate after init)
    if (adsManager && adsManager.getCuePoints()) {
      // Set cue points for Smart TV ad manager, if initialized
      // Dev Note: On Smart TV platforms, ad cue points must be tracked explicitly to handle mid-roll transitions,
      // seeking across ad breaks, and watched/unwatched ad state. The IMA SDK may not consistently emit ad start events
      // in all edge cases (e.g., fast seeks or slow buffer conditions), so SmartTvAdManager uses cue points to anticipate
      // upcoming ad breaks and manage state transitions proactively.
      if (smartTvAdManager) {
        smartTvAdManager.adCuePoints = adsManager.getCuePoints();
      }
      eventEmitter.publish('adcuepoints', adsManager.getCuePoints());
    }

    // Compute preroll skip rule dynamically to avoid stale values on recreation

    const shouldSkipPreroll = isContinueWatchingSession() && hasExplicitPlayAdsAfterTime && needsAdsManagerRecreation;

    if (shouldSkipPreroll) {
      logger.log(
        '[IMA CSAI][Smart TV] Continue watching detected - skipping ads manager start and recreating for mid-rolls',
      );
      // Dev Note: Change state to 'loaded' to simulate pre-roll completion
      changeState('loaded');
      recreateAdsManagerForContinueWatching();
      return;
    }

    startAdsManager(adsManager);
  }

  /**
   * Recreates the ads manager for Smart TV continue watching sessions after pre-roll completion.
   * This enables proper mid-roll handling by setting playAdsAfterTime to initialPlaybackTime.
   */
  function recreateAdsManagerForContinueWatching(): void {
    // Early exit if not on Smart TV or not in pre-roll phase
    if (isSmartTV && isMidrollAd) {
      return;
    }

    logger.log('[IMA CSAI][Smart TV] Starting ads manager recreation for continue watching mid-rolls');

    // Get relevant cue point for resume and update initial playback time if needed
    let adjustedInitialPlaybackTime = playbackOptions?.initialPlaybackTime || 0;
    if (smartTvAdManager) {
      const relevantCuePoint = smartTvAdManager.getRelevantCuePointOnResume(adjustedInitialPlaybackTime);
      if (relevantCuePoint !== 0) {
        adjustedInitialPlaybackTime = relevantCuePoint;
        logger.log(
          `[IMA CSAI][Smart TV] Adjusted initial playback time to ${adjustedInitialPlaybackTime}s based on relevant cue point`,
        );
      }
    }

    // Mark pre-roll phase as completed and reset continue watching state
    needsAdsManagerRecreation = false;

    cleanupImaResources();

    // Reset ad break state
    activeAdBreakInfo = undefined;
    isAdBreakStarted = false;

    logger.log('[IMA CSAI][Smart TV] Reinitializing IMA with playAdsAfterTime for continue watching');

    // Reinitialize IMA with new settings
    setupIMA();

    // Request ads - the playAdsAfterTime will be set during ads manager loaded callback
    requestAdsWithPlayAdsAfterTime(adRequest, adjustedInitialPlaybackTime);

    // Mark watched cues up to the adjusted initial playback time
    if (smartTvAdManager && adjustedInitialPlaybackTime > 0) {
      smartTvAdManager.markWatchedCuesUpTo(adjustedInitialPlaybackTime);
      logger.log(`[IMA CSAI][Smart TV] Marked cues as watched up to ${adjustedInitialPlaybackTime}s`);
    }

    // Use a short delay to ensure video element is ready
    Promise.resolve().then(() => {
      // Resume content playback after ads manager recreation
      onContentResumeRequested();
      // Dev Note: change state to 'completed' to simulate pre-roll completion
      changeState('completed');
      logger.debug(`[IMA CSAI][Smart TV] Content resumed after ads manager recreation`);
    }); // Resume content playback after ads manager recreation
  }

  const toAdBreakInfo = (adEvent: any, adsManagerInfo: any): AdBreakInfo => {
    return createCsaiAdBreakInfo(adEvent, adsManagerInfo);
  };

  const toAdInfo = (adEvent: any, adsManagerInfo: any, adBreakInfo?: AdBreakInfo): AdInfo => {
    return createCsaiAdInfo(adEvent, adsManagerInfo, adBreakInfo, {
      technology: 'client-side',
      stitcher: 'Google IMA SDK',
      managerName: 'Google IMA SDK',
      managerVersion: imaSDK.VERSION,
      adTagUrl: adRequest.adTagUrl,
    });
  };

  /**
   * Observe IMA Ad event and appropriate emit `AdPlayer` events
   * @param adEvent IMA Ad event
   */
  function onAdEvent(adEvent: any): void {
    switch (adEvent.type) {
      case imaSDK.AdEvent.Type.AD_BREAK_READY: {
        handleAdBreakReady();
        break;
      }
      case imaSDK.AdEvent.Type.LOADED:
        handleAdLoaded(adEvent);
        break;
      case imaSDK.AdEvent.Type.STARTED:
        handleAdStartedEvent(adEvent);
        break;
      case imaSDK.AdEvent.Type.RESUMED:
        handleAdResumedEvent();
        break;
      case imaSDK.AdEvent.Type.AD_BUFFERING:
        handleAdBuffering();
        break;
      case imaSDK.AdEvent.Type.FIRST_QUARTILE:
        handleFirstQuartile(adEvent);
        break;
      case imaSDK.AdEvent.Type.THIRD_QUARTILE:
        onAdTrackingEvent(AdTrackingEvent.AD_THIRD_QUARTILE_EVENT);
        break;
      case imaSDK.AdEvent.Type.AD_PROGRESS:
        onAdProgressEvent(adEvent);
        break;
      case imaSDK.AdEvent.Type.PAUSED:
        changeState('paused');
        break;
      case imaSDK.AdEvent.Type.SKIPPED:
        onAdSkippedEvent();
        break;
      case imaSDK.AdEvent.Type.COMPLETE:
        nonFatalAllAdsCompletedEvent = true;
        onAdCompleteEvent();
        break;
      case imaSDK.AdEvent.Type.ALL_ADS_COMPLETED:
        handleAllAdsCompletedEvent();
        break;
      case imaSDK.AdEvent.Type.LOG:
        reportNonFatalAdErrors(adEvent);
        break;
      case imaSDK.AdEvent.Type.MIDPOINT:
        onAdTrackingEvent(AdTrackingEvent.AD_MIDPOINT_EVENT);
        break;
      case imaSDK.AdEvent.Type.CLICK:
        onAdTrackingEvent(AdTrackingEvent.AD_CLICK);
        break;
      case imaSDK.AdEvent.Type.VIDEO_CLICKED:
        onAdTrackingEvent(AdTrackingEvent.AD_VIDEO_CLICKED);
        break;
      case imaSDK.AdEvent.Type.VIDEO_ICON_CLICKED:
        onAdTrackingEvent(AdTrackingEvent.AD_VIDEO_ICON_CLICKED);
        break;
      case imaSDK.AdEvent.Type.IMPRESSION:
        onAdTrackingEvent(AdTrackingEvent.AD_IMPRESSION);
        break;
      case imaSDK.AdEvent.Type.USER_CLOSE:
        onAdTrackingEvent(AdTrackingEvent.AD_USER_CLOSE);
        break;
      case imaSDK.AdEvent.Type.INTERACTION:
        onAdTrackingEvent(AdTrackingEvent.AD_INTERACTION);
        break;
      case imaSDK.AdEvent.Type.VOLUME_CHANGED:
        onAdTrackingEvent(AdTrackingEvent.AD_VOLUME_CHANGED);
        break;
      case imaSDK.AdEvent.Type.VOLUME_MUTED:
        onAdTrackingEvent(AdTrackingEvent.AD_VOLUME_MUTED);
        break;
      case imaSDK.AdEvent.Type.SKIPPABLE_STATE_CHANGED:
        onAdTrackingEvent(AdTrackingEvent.AD_SKIPPABLE_STATE_CHANGED);
        break;
    }
  }

  function reportNonFatalAdErrors(adEvent: any): void {
    if (adEvent.getAdData() && adEvent.getAdData()['adError']) {
      logger.log('aderror', adEvent.getAdData()['adError']);
      onAdNonFatalError(adEvent.getAdData()['adError']);
    }
  }

  function updateAdInfoOnStart(ad: any): void {
    if (ad && activeAdInfo) {
      activeAdInfo.mediaUrl = ad.getMediaUrl() || '';
      if (activeAdInfo.vastProperties) {
        activeAdInfo.vastProperties.bitrate = ad.getVastMediaBitrate() || 0;
      }
    }
  }

  function handleAdBreakReady(): void {
    const shouldStartAd = getAdfrequencyController().evaluateOpportunity() ?? true;
    if (!shouldStartAd) {
      eventEmitter.publish(AdEvent.ADVERT_SKIP_AFC_POLICY, activeAdBreakInfo);
      adsManager?.discardAdBreak();
      logger.debug('[IMA CSAI][SMART TV] Skipping ad break due to AFC policy');
      completeAdBreak();
      return;
    }
    adsManager?.start();
  }

  function handleAdLoaded(adEvent: any): void {
    activeAdBreakInfo = toAdBreakInfo(adEvent, adsManager);
    isMidrollAd = activeAdBreakInfo && activeAdBreakInfo.contentTimePosition === AdContentTimePosition.MIDROLL;

    // Dev Note: When a user seeks across an unwatched mid-roll ad cue,
    // the IMA SDK trigger a LOADED event at that cue point.
    // To ensure proper ad playback, we reset the video element's position to 0
    // and let IMA take control from the start of the ad break.
    if (isMidrollAd && smartTvAdManager?.isSeeking) {
      logger.debug('[IMA CSAI][SMART TV] Resetting video position after seek-triggered ad load');
      videoElement.currentTime = 0;
    } else {
      //check for continue watching session and recreate ads manager if needed
      checkContinueWatchingSession(activeAdBreakInfo);
    }
    onAdLoadedEvent(adEvent);
  }

  function handleAdStartedEvent(adEvent: any): void {
    nonFatalAllAdsCompletedEvent = false;
    //Note: Resolve the buffering event at the ad start.
    eventEmitter.publish('bufferingstatechanged', BUFFERING_STATES.INACTIVE);
    onAdStartedEvent(adEvent);
  }

  function handleAdResumedEvent(): void {
    if (isBuffering) {
      isBuffering = false;
      changeState('buffering');
    }
    changeState('resumed');
    onAdTrackingEvent(AdTrackingEvent.AD_RESUMED_EVENT);
  }

  function handleAllAdsCompletedEvent(): void {
    /**
     * DEV NOTE: ALL_ADS_COMPLETED Event Handling
     *
     * IMA CSAI triggers ALL_ADS_COMPLETED in these scenarios:
     * 1. All ads in a break complete normally
     * 2. Non-fatal errors occur (e.g., no valid ads in VMAP)
     *
     * Smart TV platforms requires custom content resumption here due to
     * unreliable `onContentResumeRequested` callback behavior.
     */
    if (!nonFatalAllAdsCompletedEvent && isSmartTV) {
      completeAdBreak();
    }
  }

  // Standard onAdLoadedEvent
  function onAdLoadedEvent(adEvent: any): void {
    activeAdBreakInfo = toAdBreakInfo(adEvent, adsManager);
    activeAdInfo = toAdInfo(adEvent, adsManager, activeAdBreakInfo);
    duration = activeAdInfo.durationMs ? activeAdInfo.durationMs : 0;
    changeState('loaded');
    eventEmitter.publish(AdEvent.ADVERT_LOADED, activeAdInfo);

    if (isMidrollAd) {
      initSmartTvMidRollAdTracking();
    }
  }

  /**
   * Handles continue watching logic specific to pre-roll ad scenarios.
   *
   * This function is triggered during an ad break and checks whether the session
   * is a continue-watching session and the current ad is a pre-roll.
   * If the initial playback time falls within the early content range (i.e., before the first mid-roll),
   * it allows the pre-roll to play and skips reinitializing the IMA player.
   *
   * @param adBreakInfo - Metadata about the current ad break, including cue point timing.
   */
  function checkContinueWatchingSession(adBreakInfo?: AdBreakInfo): void {
    if (!needsAdsManagerRecreation || adBreakInfo?.contentTimePosition !== AdContentTimePosition.PREROLL) {
      return;
    }

    const initialTime = playbackOptions?.initialPlaybackTime ?? 0;

    if (smartTvAdManager?.isPlaybackInEarlyContentRange(initialTime)) {
      // Handle pre-roll ad logic for early continue-watching case
      needsAdsManagerRecreation = false; // No need to recreate ads manager
    }
  }

  function initSmartTvMidRollAdTracking(): void {
    /**
     * Dev Note: SmartTV Video Element Management
     *
     * On SmartTV platforms, the IMA SDK takes full control of the video element during ads.
     * Therefore, we must detach the Shaka (content) player and let the ad manager operate on the element.
     * The SmartTvAdManager also tracks cue points and manages seek-sensitive logic.
     */

    // Start progress tracking for midroll ads
    if (smartTvAdManager && !smartTvAdManager.isSeeking && activeAdBreakInfo) {
      smartTvAdManager.onAdLoadedEvent(activeAdBreakInfo);
    }
  }

  function handleAdBuffering(): void {
    isBuffering = true;
    changeState('buffering');
  }

  function handleFirstQuartile(adEvent: any): void {
    // from IMA documenation:
    // In certain scenarios, the SDK does not know for sure how many ads are contained within this ad pod.
    // These scenarios include ad pods, which are multiple ads within a single ad tag.
    // In these scenarios, the first few AdEvents fired (AD_METADATA, LOADED, and more.) may have
    // just the total number of ad tags from the playlist response.
    // We recommend using the FIRST_QUARTILE event as the event in which publishers pull
    // information from this object and update the visual elements of the player, if any.
    if (activeAdBreakInfo && adEvent.getAd()) {
      activeAdBreakInfo.totalAds = adEvent.getAd().getAdPodInfo().getTotalAds();
    }
    onAdTrackingEvent(AdTrackingEvent.AD_FIRST_QUARTILE_EVENT);
  }

  // Simplified onAdStartedEvent function
  function onAdStartedEvent(adEvent: any): void {
    isBuffering = false;
    const ad = adEvent.getAd();
    if (isSmartTV && smartTvAdManager && activeAdBreakInfo) {
      // Gracefully skip ads if skippable (especially important for SmartTV platforms where no native support for Skippable ads by IMA CSAI SDK)
      checkSkipAds();

      !isAdBreakStarted && onContentPauseRequested();
      // Dev Note: When the playhead naturally approaches a mid-roll ad cue (i.e., not during a seek),
      // we reset the video element to 0 to allow the IMA SDK to take full control and
      // play the ad from the beginning without timing drift or desync issues.
      // This is specific to Smart TVs where a single video element is reused for both content and ads.
      if (isMidrollAd) {
        if (!smartTvAdManager.isSeeking) {
          videoElement.currentTime = 0;
        }
        videoElement.play();
        logger.debug(`[IMA CSAI][Smart TV] resetting video element to 0 with linear seek`);
      }
    }
    changeState('started');
    updateAdInfoOnStart(ad);
    eventEmitter.publish(AdEvent.ADVERT_START, activeAdInfo);
  }

  function onAdProgressEvent(adEvent: any): void {
    const adData = adEvent.getAdData();
    if (!adData) {
      return;
    }
    const { totalAds, adPosition, duration, currentTime: adCurrentTime } = adData;

    const currentAdTime = adData.currentTime;
    currentTime = currentAdTime;
    const isLastAd = totalAds === adPosition;
    const adRemainingMs = adsManager.getRemainingTime() * 1000;

    changeState('playing');
    if (activeAdBreakInfo) {
      if (isLastAd) {
        activeAdBreakInfo.durationMs = duration * 1000;
        activeAdBreakInfo.remainingTimeMs = adRemainingMs;
        completedAdsDuration = 0;
      } else {
        const adBreakPlayedTimeMs = completedAdsDuration + adCurrentTime * 1000;

        activeAdBreakInfo.remainingTimeMs = Math.max(0, activeAdBreakInfo.durationMs - adBreakPlayedTimeMs);
      }
    }
    if (activeAdInfo) {
      activeAdInfo.adBreakInfo = activeAdBreakInfo;
      activeAdInfo.remainingTimeMs = adRemainingMs;
    }

    eventEmitter.publish('adprogressupdate', activeAdInfo, currentAdTime);
  }

  function onAdCompleteEvent(): void {
    completedAdsDuration = completedAdsDuration + (activeAdInfo?.durationMs || 0);

    const isLastAdInBreak = activeAdBreakInfo?.totalAds === activeAdInfo?.sequence;

    eventEmitter.publish(AdEvent.ADVERT_END, activeAdInfo);

    // Smart TV Continue Watching: Handle pre-roll completion differently
    if (isSmartTV && isLastAdInBreak) {
      const isPrerollAd = activeAdBreakInfo?.contentTimePosition === AdContentTimePosition.PREROLL;

      if (isPrerollAd && needsAdsManagerRecreation) {
        logger.log('[IMA CSAI][Smart TV] Pre-roll completed in continue watching - initiating ads manager recreation');
        // Make sure to mark the ad break as completed
        eventEmitter.publish(AdEvent.ADVERT_BREAK_END, activeAdBreakInfo);

        // Mark pre-roll phase as completed but don't resume content yet
        activeAdInfo = undefined;

        // Recreate ads manager with playAdsAfterTime for proper mid-roll handling
        recreateAdsManagerForContinueWatching();
        return;
      }
    }

    if (isSmartTV && !isLastAdInBreak) {
      //Dev Note: For Smart TV, only process completion logic on the last ad in the break
      logger.debug(`[IMA CSAI][Smart TV] ad ${activeAdInfo?.sequence}/${activeAdBreakInfo?.totalAds} completed`);
      return;
    }

    // Complete the ad break (for non-Smart TV or last ad in Smart TV break)
    completeAdBreak();
  }

  /**
   * Handles the completion of a mid-roll ad on Smart TV platforms.
   *
   * This function ensures that after a mid-roll ad finishes, content playback resumes
   * only after a short delay. The delay allows the IMA SDK to fully release the video element,
   * preventing black screens or resume failures on certain Smart TV models.
   *
   * The delay duration is configurable via smartTvAdPlaybackOptions and can be scaled
   * for mid-roll transitions using midrollToContentTransitionFactor.
   *
   * Should be called wherever mid-roll ad completion logic is needed (ad break, skip, error, etc).
   */
  function handleMidrollAdCompletion(): void {
    // Dev Note: incase of Ad Error or Ad Skip during midroll, we need to ensure content resumes properly.
    if (isMidrollAd || smartTvAdManager?.isMidrollAd) {
      setTimeout(() => {
        // Ensure IMA SDK has fully released the video element before resuming content
        smartTvAdManager?.onAdEventEnd();
      }, smartTvPlaybackDelayMs * midrollToContentTransitionFactor);
    }
  }

  /**
   * Handles the completion of an ad break
   */
  function completeAdBreak(): void {
    /**
     * Dev note: SmartTVs use different ad integration approach
     *
     * SmartTVs have a single media element limitation where standard IMA CSAI callbacks
     * (onContentPauseRequested/onContentResumeRequested) don't help.
     * Instead, we use the ad complete event to custom resume of the content playback.
     */
    if (isSmartTV) {
      videoElement.pause();

      handleMidrollAdCompletion();
      onContentResumeRequested();
    }

    changeState('completed');

    activeAdInfo = undefined;

    logger.debug(`[IMA CSAI] ${isSmartTV ? 'Smart TV' : 'Standard'} ads completed`);
  }

  /**
   * Dev Note: Handles SmartTV-specific logic for skipping skippable ads.
   *
   * On Smart TV platforms, when an ad is flagged as skippable (`isSkippable`),
   * we must manually trigger ad break discard and resume content playback,
   * for pre/mid-rolls where the transition needs to be controlled due to
   * platform limitations (e.g., no dual video element support).
   *
   * This function ensures:
   * - Ad break is explicitly discarded via `adsManager.discardAdBreak()`.
   * - Smart TV content player resumes via `handleContentResume()`.
   * - Playback state is updated accordingly and control is handed back to content.
   */
  function checkSkipAds(): void {
    if (activeAdInfo && activeAdInfo.isSkippable) {
      adsManager && adsManager.discardAdBreak();

      handleMidrollAdCompletion();
      changeState('completed');
      onContentResumeRequested();
      logger.debug(`[IMA CSAI][SmartTV] Ad is skippable, skipping ad at ${activeAdInfo}`);
    }
  }

  function onAdSkippedEvent(): void {
    if (activeAdInfo && activeAdBreakInfo) {
      completedAdsDuration = updateAdBreakAfterInterruption(activeAdInfo, activeAdBreakInfo, completedAdsDuration);
    }
    changeState('skipped');
    eventEmitter.publish(AdEvent.ADVERT_BREAK_END, activeAdInfo);
    onAdTrackingEvent(AdTrackingEvent.AD_SKIPPED_EVENT);
    activeAdInfo = undefined;
  }

  function onAdTrackingEvent(adTrackingEvent: AdTrackingEvent): any {
    logger.log(`IMA: ad event "${adTrackingEvent}"`);
    eventEmitter.publish(AdEvent.ADVERT_TRACKING_EVENT, adTrackingEvent);
  }

  // Standard onAdError function (no complex recovery needed)
  async function onAdNonFatalError(adErrorEvent: any): Promise<void> {
    logger.log('[IMA CSAI][Smart TV] Ad error occurred:', adErrorEvent);

    changeState('failed', adErrorEvent);
    eventEmitter.publish('aderror', adErrorEvent);
    handleAdFailureContentResumption();
  }

  // Standard onAdError function (no complex recovery needed)
  async function onAdError(adErrorEvent: any): Promise<void> {
    if (activeAdInfo && activeAdBreakInfo) {
      completedAdsDuration = updateAdBreakAfterInterruption(activeAdInfo, activeAdBreakInfo, completedAdsDuration);
    }
    completeAdRequest(false);
    logger.debug('[IMA CSAI][Smart TV] Ad error occurred:', adErrorEvent.getError());

    changeState('failed', adErrorEvent.getError());
    eventEmitter.publish('aderror', adErrorEvent.getError());
    handleAdFailureContentResumption();
  }

  async function handleAdFailureContentResumption(): Promise<void> {
    //Ad error on preroll/midroll with Smart TV, gracefully transition to the content playback if it's the last ad.
    const isLastAdInBreak = activeAdBreakInfo?.totalAds === activeAdInfo?.sequence;
    if (smartTvAdManager && isLastAdInBreak) {
      // Dev Note: For Smart TV mid-roll ad errors, we must ensure content resumes properly.
      // in that case,we have no active ad break info as the ad failed to start. So we get the nearest upcoming cue from the current playback time.
      smartTvAdManager?.getNearestUpcomingCue(videoElement.currentTime);
      logger.log('[IMA CSAI][Smart TV] Ad error occurred during midroll, transitioning to content playback');
      handleMidrollAdCompletion();
      onContentResumeRequested();
    }
  }

  function onContentPauseRequested(): void {
    isAdBreakStarted = true;
    eventEmitter.publish(AdEvent.ADVERT_BREAK_START, activeAdBreakInfo);
    eventEmitter.publish('adstatechanged', true);
    eventEmitter.publish('adplayercontextwillchange');
  }

  function onContentResumeRequested(): void {
    if (isAdBreakStarted) {
      eventEmitter.publish(AdEvent.ADVERT_BREAK_END, activeAdBreakInfo);
      isAdBreakStarted = false;
      isMidrollAd = false;
    }
    completedAdsDuration = 0;
    // Dev Note: Smart TV platforms with Ad Manager
    // When an ad load timeout or error occurs, the corresponding cue should
    // be marked as "watched" for IMA CSAI mid-rolls.
    // Updating the cue point here ensures proper handling of skipped/failed ads.
    if (smartTvAdManager) {
      // Calculate the cue point based on the ad break start time offset
      const cuePoint = activeAdBreakInfo && activeAdBreakInfo?.adBreakStartTimeOffsetMs / 1000;
      smartTvAdManager.addWatchedCuePoint(cuePoint);
      logger.debug(`[IMA CSAI][Smart TV] Ad Event started at cue point: ${cuePoint}s`);
    }
    activeAdBreakInfo = undefined;
    eventEmitter.publish('adstatechanged', false);
    eventEmitter.publish('adplayercontextwillchange');
  }

  function cleanupImaResources(): void {
    try {
      adEvents.forEach(eventType => {
        if (adsManagerEventListener) {
          adsManager?.removeEventListener(eventType, adsManagerEventListener, false);
        }
      });
      adsManager?.destroy();
    } catch (e) {
      logger.error(`[IMA CSAI][Smart TV] AdsManager cleanup failed: ${e}`);
    } finally {
      adsManager = null;
      adsManagerEventListener = null;
    }

    try {
      adsLoader?.removeEventListener(imaSDK.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED, onAdsManagerLoaded, false);
      adsLoader?.removeEventListener(imaSDK.AdErrorEvent.Type.AD_ERROR, onAdError, false);
      adsLoader?.destroy();
    } catch (e) {
      logger.error(`[IMA CSAI][Smart TV] AdsLoader cleanup failed: ${e}`);
    } finally {
      adsLoader = null;
    }

    try {
      adDisplayContainer?.destroy();
    } catch (e) {
      logger.error(`[IMA CSAI][Smart TV] AdDisplayContainer cleanup failed: ${e}`);
    } finally {
      adDisplayContainer = null;
    }
  }

  setupIMA();

  return imaPlayer;
}
